import React, { useContext, useEffect, useRef } from "react"
import { Box, Button, Chip, Container, Divider, Grid, ListItem, Typography } from "@mui/material"
import { useApiGet, useApiGetBlob } from "auth/useApi2"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import PaperBox from "components/PaperBox"
import { SkeletonCard } from "components/skeleton/SkeletonCard"
import { TinyMCEViewer } from "components/TinyMCE/TinyMCEViewer"
import { apiEndpoints } from "generated/apiEndpoints"
import "moment/locale/sv"
import "moment/locale/da"
import MUIRichTextEditor from "mui-rte"
import Moment from "react-moment"
import { StyledGrid, StyledPaperBox } from "../../../../components/NewsRead.styled"
import { isJson } from "components/TinyMCE/helpers"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDownToBracket, faDownload } from "@fortawesome/pro-solid-svg-icons"

const BodyRichContent = ({ content }) => {
  return <MUIRichTextEditor defaultValue={content} readOnly={true} toolbar={false} />
}

const NewsRead = ({ match }) => {
  const loadingContext = useContext(GlobalLoaderContext)
  const [articleResult, getArticle] = useApiGet()
  const [getAttachmentResult, getAttachment] = useApiGetBlob()
  const editorRef = useRef(null)
  const { t } = useTranslation("translation")

  useEffect(() => {
    getArticle(apiEndpoints.news.getarticle.replace("{id}", match.params.id))
  }, [getArticle, match.params.id])

  useEffect(() => {
    loadingContext.setLoading(() => articleResult.status === 1)
  })

  const downloadAttachment = React.useCallback(
    (id, fileName) => {
      getAttachment(apiEndpoints.news.getattachment.replace("{id}", id), fileName)
    },
    [getAttachment]
  )

  const article = articleResult.data

  return (
    <>
      <Container>
        <Grid container spacing={4} direction="row" justifyContent="space-between">
          <Grid item xs={12} md={8}>
            {articleResult.status === 1 && <SkeletonCard />}
            {articleResult.status === 2 ? (
              <>
                <StyledPaperBox>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h2" gutterBottom>
                      {article.subject}
                    </Typography>
                    {article.priorityEndDate && (
                      <Typography variant="bodySmall" color="primary" gutterBottom>
                        <Chip color="secondary" label={t("global.important", "Important") + "!"} />
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="paragraphLarge" gutterBottom>
                    {article.subTitle}
                  </Typography>
                  {articleResult.status === 2 ? (
                    isJson(articleResult.data.body) ? (
                      <BodyRichContent content={articleResult.data.body} />
                    ) : (
                      <TinyMCEViewer initVal={articleResult.data.body} editorRef={editorRef} />
                    )
                  ) : (
                    <></>
                  )}
                  <Box my={2}>
                    <Divider />
                    <Box my={2}>
                      <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid>
                          {t("global.by", "By")} {article.publishedByUserFullName}
                          {" • "}
                          <Moment format="LL" globallocale="sv">
                            {article.publishedDate}
                          </Moment>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </StyledPaperBox>
              </>
            ) : null}
          </Grid>
          <Grid item xs={12} md={4}>
            {articleResult.status === 1 && <SkeletonCard />}
            {articleResult.status === 2 && articleResult.data.attachments.length > 0 && (
              <PaperBox>
                <Typography variant="h4">{t("news.file_attachments", "File attachments")}</Typography>
                {articleResult.data?.attachments?.map((item, key) => {
                  return (
                    <ListItem key={key} divider disableGutters>
                      <StyledGrid container alignItems="center">
                        <Grid item xs={6}>
                          {item.name}
                        </Grid>
                        <Grid item xs={6}>
                          <Box display="flex" justifyContent="flex-end">
                            <Button
                              onClick={() => {
                                downloadAttachment(item.id, item.name)
                              }}
                              color="primary"
                              variant="contained"

                            >
                              {t("global.buttons.download")}
                              <FontAwesomeIcon
                                icon={faArrowDownToBracket}
                              />
                            </Button>
                          </Box>
                        </Grid>
                      </StyledGrid>
                    </ListItem>
                  )
                })}
              </PaperBox>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default NewsRead
